import { useState } from 'react';
import type { OfferPreviewItem, OfferPreviewSkipReason } from '../../types/offers';
import { formatAed } from './offerReviewModel';

/** Rows shown before "Show all": car-specific services can have 38+ variants. */
export const VARIANT_ROWS_COLLAPSED = 6;

/** Floored percent for display: "<1" when there is a saving the floor rounds to 0 (API keeps the int). */
export function percentText(percentOff: number, saving: number): string {
  return percentOff <= 0 && saving > 0 ? '<1' : String(percentOff);
}

const SKIP_TEXT: Record<OfferPreviewSkipReason, string> = {
  BELOW_MIN_PRICE: 'No discount: it would leave less than AED 1',
  NO_DISCOUNT: 'No discount',
};

/**
 * Decision "4 preview" — one service's prices under the offer rule, per bookable variant:
 * Before → After (saving, %). Every figure comes from the server. Mirrors the partner portal's
 * `components/offers/VariantPriceTable.tsx` (the portals share no code); #997 (admin edits offers)
 * reuses this one.
 */
export default function VariantPriceTable({ item }: { item: OfferPreviewItem }) {
  const [open, setOpen] = useState(true);
  const [showAll, setShowAll] = useState(false);
  const rows = item.variants;
  const visible = showAll ? rows : rows.slice(0, VARIANT_ROWS_COLLAPSED);
  const maxSaving = rows.reduce((m, r) => Math.max(m, r.discounted ? r.saving : 0), 0);
  const pct = percentText(item.maxPercentOff, maxSaving);
  const summary = rows.length === 0
    ? 'No price yet'
    : rows.length === 1
      ? `1 price · ${pct}% off`
      : `${rows.length} prices · up to ${pct}% off`;

  return (
    <div className="vpt" data-testid={`variant-prices-${item.offeringId}`}>
      <button type="button" className="vpt__toggle" aria-expanded={open} onClick={() => setOpen((o) => !o)}>
        <span className="vpt__chevron" aria-hidden="true">{open ? '▾' : '▸'}</span>
        <span className="vpt__name">{item.name}</span>
        <span className="vpt__summary">{summary}</span>
        {item.skippedCount > 0 && <span className="admin-badge warning">{item.skippedCount} with no discount</span>}
        {!item.bookable && <span className="admin-badge warning">Not bookable</span>}
      </button>
      {open && rows.length > 0 && (
        <>
          <table className="admin-table vpt__table">
            <thead>
              <tr>
                <th>Variant</th>
                <th>Before</th>
                <th>After</th>
                <th>Saving</th>
              </tr>
            </thead>
            <tbody>
              {visible.map((r, i) => (
                <tr key={r.variantId ?? `flat-${i}`} className={r.discounted ? undefined : 'vpt__row--skipped'}
                  data-testid={r.discounted ? 'variant-row' : 'variant-row-skipped'}>
                  <td>{r.label ?? 'Standard price'}</td>
                  <td>{r.discounted ? <s>{formatAed(r.listPrice)}</s> : formatAed(r.listPrice)}</td>
                  <td style={{ fontWeight: 600 }}>{formatAed(r.offerPrice)}</td>
                  <td>
                    {r.discounted
                      ? `${formatAed(r.saving)} (${percentText(r.percentOff, r.saving)}%)`
                      : <span className="vpt__warn" role="note">⚠ {SKIP_TEXT[r.skipReason ?? 'NO_DISCOUNT']}</span>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {rows.length > VARIANT_ROWS_COLLAPSED && (
            <button type="button" className="vpt__more" onClick={() => setShowAll((s) => !s)}>
              {showAll ? 'Show fewer' : `Show all ${rows.length}`}
            </button>
          )}
        </>
      )}
    </div>
  );
}
